/**
 * Shared, DB-driven pricing sections used across Home, /pricing and /for-labs.
 *
 *   <LabPlansSection />          — subscription plans for labs (monthly/annual toggle)
 *   <PatientPricingSection />    — smart-report unit price + wallet recharge packs
 *   <TaxFooter />                — GST inclusive / exclusive note
 *
 * All data comes from `usePublicPricing()` → GET /api/public/platform-settings.
 * Zero hardcoded prices, feature lists, or plan names.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, TrendingUp, Building2, Wallet as WalletIcon, Loader2 } from "lucide-react";
import { usePublicPricing, currencySymbol } from "@/lib/pricing";

/* --------------------------- Lab subscription plans --------------------------- */
export function LabPlansSection({
  heading = "For laboratories · Choose the plan that fits your volume.",
  eyebrow = "For laboratories",
  ctaTo = "/for-labs",
  ctaLabel = "Get started",
  freeLabel = "Start free",
  className = "",
}) {
  const { pricing, loading } = usePublicPricing();
  const [billing, setBilling] = useState("monthly");
  const symbol = currencySymbol(pricing);
  const plans = pricing?.subscription_plans || [];

  const featuredPlanId = useMemo(() => {
    if (!plans.length) return null;
    const popular = plans.find((p) => p.popular);
    if (popular) return popular.id;
    return plans[Math.min(1, plans.length - 1)]?.id;
  }, [plans]);

  if (loading) {
    return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40" /></div>;
  }
  if (!plans.length) return null;

  return (
    <section className={`px-6 py-16 ${className}`} data-testid="pricing-lab-plans">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-efgh-blue font-heading font-bold flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> {eyebrow}</div>
            <h2 className="mt-1 font-heading font-bold text-2xl sm:text-3xl text-ink">{heading}</h2>
          </div>
          <div className="flex items-center gap-1 p-1 rounded-full bg-muted" data-testid="billing-toggle">
            {["monthly", "annual"].map((b) => (
              <button
                key={b}
                type="button"
                data-testid={`billing-${b}`}
                onClick={() => setBilling(b)}
                className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-full font-semibold transition-all ${billing === b ? "bg-white text-ink shadow-sm" : "text-ink/50 hover:text-ink"}`}
              >
                {b}{b === "annual" && <span className="ml-1 text-efgh-orange">·  2 mo. free</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
          {plans.map((p) => {
            const price = billing === "annual" ? p.annual_price : p.monthly_price;
            const period = billing === "annual" ? "/ year" : "/ month";
            const featured = p.id === featuredPlanId;
            return (
              <Card
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`rounded-2xl p-6 relative flex flex-col ${featured ? "ring-2 ring-efgh-orange shadow-lg lg:-translate-y-1" : ""}`}
              >
                {featured && (
                  <span className="absolute -top-3 right-6 bg-efgh-orange text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold">Most popular</span>
                )}
                <div className="font-heading font-bold text-xl text-ink">{p.name}</div>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-heading font-bold text-4xl text-ink">{symbol}{Number(price || 0).toLocaleString()}</span>
                  <span className="text-sm text-ink/50">{period}</span>
                </div>
                <div className="mt-1 text-xs text-ink/60">
                  {(p.reports_included || 0).toLocaleString()} reports included · then {symbol}{p.per_report_overage}/report
                </div>
                <ul className="mt-5 space-y-2 text-sm text-ink/80 flex-1">
                  {(p.features || []).map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-efgh-green shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to={ctaTo} className="block mt-6">
                  <Button data-testid={`plan-cta-${p.id}`} className={`w-full h-11 ${featured ? "bg-efgh-orange hover:bg-efgh-orange/90 text-white" : "bg-ink hover:bg-ink/90 text-white"}`}>
                    {Number(price) === 0 ? freeLabel : ctaLabel}
                  </Button>
                </Link>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Patient wallet packs --------------------------- */
export function PatientPricingSection({
  heading = "Pay per report. Top up as you go.",
  eyebrow = "For patients & individuals",
  ctaTo = "/register?role=patient",
  ctaLabel = "Sign up to recharge",
  className = "",
}) {
  const { pricing, loading } = usePublicPricing();
  const symbol = currencySymbol(pricing);
  const packs = pricing?.recharge_packs || [];
  const patientPrice = pricing?.patient?.smart_report_price ?? 15;

  if (loading) return null;

  return (
    <section className={`px-6 py-14 ${className}`} data-testid="pricing-patient">
      <div className="max-w-6xl mx-auto">
        <div className="text-[11px] uppercase tracking-widest text-efgh-green font-heading font-bold flex items-center gap-1.5"><WalletIcon className="w-3.5 h-3.5" /> {eyebrow}</div>
        <h2 className="mt-1 font-heading font-bold text-2xl sm:text-3xl text-ink">{heading}</h2>
        <p className="mt-3 text-sm sm:text-base text-ink/60 max-w-2xl">
          Each AI-generated Smart Report costs <b data-testid="patient-smart-report-price">{symbol}{patientPrice}</b>. Add coins to your wallet in one tap — larger packs come with bonus credits.
        </p>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {packs.map((p) => (
            <Card
              key={p.id}
              data-testid={`pack-card-${p.id}`}
              className={`rounded-2xl p-5 relative flex flex-col ${p.popular ? "ring-2 ring-efgh-orange shadow-md" : ""}`}
            >
              {p.popular && <span className="absolute -top-3 right-4 bg-efgh-orange text-white text-[10px] uppercase tracking-widest px-3 py-1 rounded-full font-bold">Best value</span>}
              <div className="text-[10px] uppercase tracking-widest text-ink/50 font-heading font-semibold">{p.label || "Pack"}</div>
              <div className="mt-2 font-heading font-bold text-3xl text-ink">{symbol}{Number(p.amount || 0).toLocaleString()}</div>
              {p.bonus_credits > 0 && (
                <div className="mt-1 text-xs text-efgh-orange font-semibold flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> +{p.bonus_credits} bonus credits
                </div>
              )}
              <div className="mt-3 text-[11px] text-ink/50 flex-1">
                ≈ {Math.floor((Number(p.amount) + Number(p.bonus_credits || 0)) / Math.max(patientPrice, 1))} smart reports
              </div>
              <Link to={ctaTo} className="block mt-4">
                <Button data-testid={`pack-cta-${p.id}`} variant="outline" className="w-full">{ctaLabel}</Button>
              </Link>
            </Card>
          ))}
          {packs.length === 0 && (
            <div className="col-span-full p-8 border border-dashed border-border rounded-2xl text-center text-ink/50">
              Recharge packs will appear here once the admin configures them.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Tax footer note --------------------------- */
export function TaxFooter({ className = "" }) {
  const { pricing } = usePublicPricing();
  const taxes = pricing?.taxes || {};
  return (
    <div className={`text-xs text-ink/50 text-center ${className}`} data-testid="pricing-tax-footer">
      {taxes.gst_included_in_price
        ? `All prices include ${taxes.tax_label || "GST"}.`
        : `All prices exclude ${taxes.tax_label || "GST"} @ ${taxes.gst_percent ?? 18}% (added at checkout).`}
    </div>
  );
}
